import { useQuery } from '@tanstack/react-query';
import type { Campaign } from '@discovr/types';
import { campaignKeys } from '../queryKeys';
import { createCampaignDetailApi } from '../api/campaignQueries';

const api = createCampaignDetailApi();

/**
 * Lightweight hook for reading the campaign header (name, platform, status).
 *
 * Uses the same query key as useCampaignDetail so if the page-level hook has
 * already fetched the campaign this returns cached data synchronously — zero
 * extra network round-trips.
 *
 * Use this in tab components that only need campaign.platform or campaign.name
 * without pulling in the full multi-query useCampaignDetail hook.
 *
 * `options.enabled` lets a caller suppress the fetch outright (e.g. the
 * public read-only share view, which has no authed campaign id to fetch).
 * Same `enabled: !!campaignId && (options?.enabled ?? true)` precedent as
 * useLastImportResult. Defaults to enabled so every existing caller is
 * unchanged.
 */
export function useCampaignHeaderQuery(campaignId: string, options?: { enabled?: boolean }) {
  return useQuery<Campaign>({
    queryKey: campaignKeys.detail(campaignId),
    queryFn: () => api.getCampaign(campaignId),
    enabled: !!campaignId && (options?.enabled ?? true),
    staleTime: 10 * 60_000,
  });
}
